import axios from "axios";
import {
  DocumentMetadata,
  Option,
  PitbullActivities,
  PitbullActivityPhysical,
  PitbullCountries,
  PitbullCountry,
  PitbullProvinces,
} from "../types";
import { getCompanyData } from "../utils";
import {
  getCountryList,
  getIndividualDocuments,
  getPhysicalActivities,
  getProvinces,
} from "../connectors/connectors";
import { FormikValues } from "formik";
import { getQuestions } from "../app/utils";

const getTemplateData = async ({
  draftUUID,
  formValues,
}: {
  draftUUID: string;
  formValues?: FormikValues | null;
}) => {
  const { favicon, formTitle, logo } = getCompanyData();

  const getDocuments = async (
    draftUUID?: string,
    cuit?: string
  ): Promise<DocumentMetadata[] | undefined> => {
    if (!draftUUID || !cuit) return;
    const { data } = await getIndividualDocuments({ draftUUID, cuit });
    return data;
  };

  const getActivities = async (): Promise<PitbullActivities> => {
    try {
      const { data } = await getPhysicalActivities();
      return data;
    } catch (error) {
      throw new Error("Error al obtener las actividades");
    }
  };

  const getCountries = async (): Promise<PitbullCountries> => {
    try {
      const { data } = await getCountryList();
      return data;
    } catch (error) {
      throw new Error("Error al obtener los países");
    }
  };

  const results = await axios.all([
    // eslint-disable-next-line
    // @ts-ignore
    getActivities(),
    // eslint-disable-next-line
    // @ts-ignore
    getCountries(),
    // eslint-disable-next-line
    // @ts-ignore
    getDocuments(draftUUID, formValues?.cuit || ""),
  ]);

  const [activities, countries, documents] = results as [
    PitbullActivities,
    PitbullCountries,
    DocumentMetadata[] | undefined
  ];

  const heritageDocuments = documents?.filter(
    ({ type }) => type === "WORTH_STATEMENT"
  );

  const activitiesOptions: Option[] = (
    activities as PitbullActivityPhysical[]
  )?.map(({ name }) => ({ value: name, label: name }));

  const countriesOptions: Option[] = countries?.map(({ name }) => ({
    value: name,
    label: name,
  }));

  const citizenshipOptions: Option[] = countries?.map(({ citizenship }) => ({
    value: citizenship,
    label: citizenship,
  }));

  const getProvincesFilteredByCountry = (country = "Argentina") => {
    const iso = getCountryIsoAlpha2(countries, country);
    const filteredProvince = provinces.filter((prov) => {
      if (prov.countryIsoAlpha2 === iso) {
        return prov;
      }
    });
    return filteredProvince;
  };

  const getCountryIsoAlpha2 = (
    countries: PitbullCountry[],
    country: string
  ) => {
    return countries.find((countryTyped) => countryTyped.name === country)
      ?.isoAlpha2;
  };

  const getProvincesByIso = async (iso?: string): Promise<PitbullProvinces> => {
    try {
      const { data } = await getProvinces(iso);
      return data;
    } catch (error) {
      throw new Error("Error al obtener las preguntas");
    }
  };

  const provinces = await getProvincesByIso();
  const questions = await getQuestions();

  return {
    favicon,
    formTitle,
    logo,
    documents,
    activitiesOptions,
    countriesOptions,
    citizenshipOptions,
    getProvincesFilteredByCountry,
    questions,
    countries,
    provinces,
    heritageDocuments,
  };
};

export default getTemplateData;
